/**
 * 組織レポート（社長向け）に載せる説明文の「たたき台」。
 *
 * 【編集方針】
 *  - 本ファイルの文字列を書き換えるだけで、PDF/HTML 両方の文言が変わる。
 *    レイアウト側（teamReportGenerator.ts）を触る必要はない。
 *  - 読み手は社長・経営層。専門用語を避け、「何が分かるか」「次に何をすればよいか」
 *    が1〜3文で伝わる長さに収める（長いとページに収まらない）。
 *  - スコアや部署名などデータ由来の文言はサーバ/算出ロジック側が持つ。ここには
 *    データに依存しない「読み方の説明」だけを置く。
 */

/** 各ページ下部に出す「この項目の見方」。空文字にするとその行は描画されない。 */
export const ORG_REPORT_NOTES = {
  /** 1-1 組織スコア */
  orgScore:
    '働きがい・つながり・離職の兆しの3つを0〜100点で表し、総合評価としてまとめています。A（80〜100）良好／B（60〜79）注意／C（40〜59）要対応／D（0〜39）緊急。まず総合点で全体像をつかみ、最も低い指標から手を打つのが基本です。',

  /** 1-2 組織タイプ */
  orgType:
    '社員一人ひとりの思考の型を組織全体で平均し、A論理・B着実・C共感・D創造の4軸で表したものです。外に張り出している軸がこの組織の強み、内側にへこんでいる軸が手薄になりやすい領域です。優劣ではなく「どこが得意な集団か」を示します。',

  /** 1-3 社員の分布 */
  distribution:
    '点はひとり分の回答傾向です。点が一箇所に集まっていれば価値観の揃った組織、広く散っていれば多様性の高い組織を意味します。散らばり自体は弱みではなく、噛み合わせを設計できれば強みになります。',

  /** 2-n つながりの構造（部署別） */
  connection:
    '円がメンバー、色のついた大きな円が相談の集まるキーパーソンです。三角形に近いほど支え合いができており、V字は一極集中、スポークは相談が薄い状態を示します。',

  /** 3-1 評価を上げるために */
  improvement:
    '改善余地の大きい順に並べています。すべてを同時に行う必要はありません。1番目から着手し、次回の診断でスコアの変化を確認してください。',

  /** 4-n 組織メンバーの各タイプ */
  memberTypes:
    'メンバー一人ひとりの思考の型を並べたものです。形が違うこと自体は問題ではありません。違いを踏まえて役割分担や伝え方を調整することが目的です。',
} as const;

/** 「つながりの構造とは」解説ページの文言。 */
export const CONNECTION_INTRO_COPY = {
  /** ページ見出しの右に出す小見出し */
  subtitle: 'この図の読み方',

  /** 導入文 */
  lead: '「仕事で困ったとき、誰に相談しますか」という問いへの回答から、部署内の相談のつながりを図にしたものです。組織図ではなく、実際に人が動いている関係を表します。',

  /** 図の見方（箇条書き） */
  legend: [
    { label: '円', text: '部署のメンバー1人を表します。' },
    { label: '色つきの大きな円', text: '相談が集まっている人（キーパーソン）です。' },
    { label: '線', text: '相談のつながり。キーパーソンへ向かう線は濃く表示されます。' },
    { label: 'キーパーソン(n)', text: 'その人に相談しているメンバーの人数です。' },
  ],

  /** 3つの型の解説。shape は CONSULT_SHAPE_META のキーと対応させること。 */
  shapes: [
    {
      shape: 'triangle' as const,
      name: '三角形（多極・支え合い）',
      text: '相談先が複数に分かれ、互いに支え合えている状態です。1人が不在でも業務が止まりにくく、最も安定しています。',
    },
    {
      shape: 'vee' as const,
      name: 'V字（一極集中・属人化）',
      text: '特定の1人に相談が集まっている状態です。その人が抜けると業務が滞るリスクがあります。',
    },
    {
      shape: 'spoke' as const,
      name: 'スポーク（相談が薄い）',
      text: 'そもそも相談のやり取りが少ない状態です。抱え込みや孤立が起きやすくなります。',
    },
  ],

  /** 締めの一文 */
  closing:
    '目指す形は三角形です。V字なら相談先を二重化する、スポークなら相談のきっかけをつくることが、次の一手になります。',
} as const;
